/**
 * Date utilities for truck scheduling operations
 * Provides date handling for both inbound and outbound operations
 * Updated for timezone awareness
 */

import { 
  format, parseISO, isValid, isAfter, isBefore, isToday, 
  addDays, setHours, setMinutes, startOfDay, endOfDay, isSameDay 
} from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { 
  formatDateInTimezone, formatDateForDisplay, formatTimeForDisplay, 
  formatDateTimeForDisplay, convertToUTC, convertFromUTC, combineDateTimeToUTC 
} from '../../../../../utils/dateService';

// Default timezone to fall back to if user preference isn't available
const DEFAULT_TIMEZONE = 'America/Chicago'; // CST/CDT

/**
 * Format a date string into a human-readable format, respecting timezone
 * @param {string|Date} date - The date to format
 * @param {string} formatStr - Format string (defaults to 'MMM dd, yyyy')
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {string} - Formatted date
 */
const formatDate = (date, formatStr = 'MMM dd, yyyy', timezone = DEFAULT_TIMEZONE) => {
  if (!date) return '';
  
  try {
    return formatDateForDisplay(date, formatStr, timezone);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format a time string into a human-readable format, respecting timezone
 * @param {string|Date} date - The date with time to format
 * @param {string} formatStr - Format string (defaults to 'h:mm a')
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {string} - Formatted time
 */
const formatTime = (date, formatStr = 'h:mm a', timezone = DEFAULT_TIMEZONE) => {
  if (!date) return '';
  
  try {
    return formatTimeForDisplay(date, formatStr, timezone);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

/**
 * Format a datetime string into a human-readable format, respecting timezone
 * @param {string|Date} date - The datetime to format
 * @param {string} formatStr - Format string (defaults to 'MMM dd, yyyy h:mm a')
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {string} - Formatted datetime
 */
const formatDateTime = (date, formatStr = 'MMM dd, yyyy h:mm a', timezone = DEFAULT_TIMEZONE) => {
  if (!date) return '';
  
  try {
    return formatDateTimeForDisplay(date, formatStr, timezone);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'Invalid datetime';
  }
};

/**
 * Check if a date is in the past
 * @param {string|Date} date - The date to check
 * @returns {boolean} - Whether the date is in the past
 */
const isPastDate = (date) => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return false;
    return isBefore(dateObj, new Date());
  } catch (error) {
    console.error('Error checking past date:', error);
    return false;
  }
};

/**
 * Check if a date is in the future
 * @param {string|Date} date - The date to check
 * @returns {boolean} - Whether the date is in the future
 */
const isFutureDate = (date) => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return false;
    return isAfter(dateObj, new Date());
  } catch (error) {
    console.error('Error checking future date:', error);
    return false;
  }
};

/**
 * Get relative day label (Today, Tomorrow, Yesterday, or formatted date)
 * @param {string|Date} date - The date to format
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {string} - Relative day label
 */
const getRelativeDay = (date, timezone = DEFAULT_TIMEZONE) => {
  if (!date) return '';
  
  try {
    // Convert to user's timezone first for accurate "today" comparisons
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(dateObj)) return 'Invalid date';
    
    // Convert UTC date to user's timezone for comparison
    const userDate = utcToZonedTime(dateObj, timezone);
    const today = utcToZonedTime(new Date(), timezone);
    
    if (isSameDay(userDate, today)) return 'Today';
    if (isSameDay(addDays(userDate, 1), today)) return 'Yesterday';
    if (isSameDay(addDays(userDate, -1), today)) return 'Tomorrow';
    
    return formatDate(date, 'MMM dd, yyyy', timezone);
  } catch (error) {
    console.error('Error getting relative day:', error);
    return 'Invalid date';
  }
};

/**
 * Group appointments/schedules by date for calendar view
 * @param {Array} items - Array of items with scheduledDate property
 * @param {string} dateField - The field name containing the date (default: 'scheduledDate')
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {Object} - Object with dates as keys and arrays of items as values
 */
const groupByDate = (items, dateField = 'scheduledDate', timezone = DEFAULT_TIMEZONE) => {
  if (!items || !Array.isArray(items)) return {};
  
  return items.reduce((acc, item) => {
    const itemDate = item[dateField];
    if (!itemDate) return acc;
    
    try {
      // Format the date in the user's timezone for consistent grouping
      const dateInUserTimezone = utcToZonedTime(
        typeof itemDate === 'string' ? parseISO(itemDate) : itemDate,
        timezone
      );
      
      const dateKey = format(dateInUserTimezone, 'yyyy-MM-dd');
      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(item);
      return acc;
    } catch (error) {
      console.error('Error grouping by date:', error);
      return acc;
    }
  }, {});
};

/**
 * Combine date and time strings into a single Date object with timezone awareness
 * @param {string} dateStr - The date string (YYYY-MM-DD)
 * @param {string} timeStr - The time string (HH:MM or HH:MM:SS)
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {Date} - Combined Date object in UTC
 */
const combineDateAndTime = (dateStr, timeStr, timezone = DEFAULT_TIMEZONE) => {
  if (!dateStr) return null;
  
  try {
    return combineDateTimeToUTC(dateStr, timeStr, timezone);
  } catch (error) {
    console.error('Error combining date and time:', error);
    return null;
  }
};

/**
 * Get the date part of a datetime string in YYYY-MM-DD format, respecting timezone
 * @param {string|Date} datetime - The datetime to extract date from (in UTC)
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {string} - Date part in YYYY-MM-DD format
 */
const getDatePart = (datetime, timezone = DEFAULT_TIMEZONE) => {
  if (!datetime) return '';
  
  try {
    // Convert UTC date to user's timezone
    const dateObj = typeof datetime === 'string' ? parseISO(datetime) : datetime;
    if (!isValid(dateObj)) return '';
    
    const userTimezoneDate = utcToZonedTime(dateObj, timezone);
    return format(userTimezoneDate, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error getting date part:', error);
    return '';
  }
};

/**
 * Get the time part of a datetime string in HH:MM format (24-hour), respecting timezone
 * @param {string|Date} datetime - The datetime to extract time from (in UTC)
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {string} - Time part in HH:MM format
 */
const getTimePart = (datetime, timezone = DEFAULT_TIMEZONE) => {
  if (!datetime) return '';
  
  try {
    // Convert UTC date to user's timezone
    const dateObj = typeof datetime === 'string' ? parseISO(datetime) : datetime;
    if (!isValid(dateObj)) return '';
    
    const userTimezoneDate = utcToZonedTime(dateObj, timezone);
    return format(userTimezoneDate, 'HH:mm');
  } catch (error) {
    console.error('Error getting time part:', error);
    return '';
  }
};

/**
 * Check if truck is scheduled for the given date, respecting timezone
 * @param {Object} truck - Truck data object with scheduled_at (UTC)
 * @param {Date} date - Date to check (local)
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {boolean} - Whether the truck is scheduled for the date
 */
const isTruckScheduledForDate = (truck, date, timezone = DEFAULT_TIMEZONE) => {
  if (!truck || !truck.scheduled_at || !date) return false;
  
  try {
    // Parse the scheduled date (in UTC)
    const scheduledDate = typeof truck.scheduled_at === 'string' 
      ? parseISO(truck.scheduled_at) 
      : truck.scheduled_at;
      
    // Convert both dates to the user's timezone for comparison
    const scheduledInUserTz = utcToZonedTime(scheduledDate, timezone);
    const dateInUserTz = utcToZonedTime(date, timezone);
      
    return isSameDay(scheduledInUserTz, dateInUserTz);
  } catch (error) {
    console.error('Error checking truck schedule date:', error);
    return false;
  }
};

/**
 * Check if two time slots overlap, with timezone awareness
 * @param {Date|string} start1 - Start of first slot (UTC)
 * @param {Date|string} end1 - End of first slot (UTC)
 * @param {Date|string} start2 - Start of second slot (UTC)
 * @param {Date|string} end2 - End of second slot (UTC)
 * @returns {boolean} - Whether the slots overlap
 */
const doTimeSlotsOverlap = (start1, end1, start2, end2) => {
  if (!start1 || !start2) return false;
  
  try {
    // Parse all dates as UTC
    const s1 = typeof start1 === 'string' ? parseISO(start1) : start1;
    const e1 = end1 ? (typeof end1 === 'string' ? parseISO(end1) : end1) : addDays(s1, 1);
    const s2 = typeof start2 === 'string' ? parseISO(start2) : start2;
    const e2 = end2 ? (typeof end2 === 'string' ? parseISO(end2) : end2) : addDays(s2, 1);
    
    // Time slots overlap if one starts before the other ends
    return (s1 < e2 && e1 > s2);
  } catch (error) {
    console.error('Error checking time slot overlap:', error);
    return false;
  }
};

/**
 * Get start of day for calendar view, respecting timezone
 * @param {Date|string} date - Date to get start of day for (UTC)
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {Date} - Start of day in UTC
 */
const getStartOfDay = (date, timezone = DEFAULT_TIMEZONE) => {
  if (!date) return startOfDay(new Date());
  
  try {
    // Parse the date
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Convert to user's timezone
    const dateInUserTz = utcToZonedTime(dateObj, timezone);
    
    // Get start of day in user's timezone
    const startOfDayInUserTz = startOfDay(dateInUserTz);
    
    // Convert back to UTC
    return zonedTimeToUtc(startOfDayInUserTz, timezone);
  } catch (error) {
    console.error('Error getting start of day:', error);
    return startOfDay(new Date());
  }
};

/**
 * Get end of day for calendar view, respecting timezone
 * @param {Date|string} date - Date to get end of day for (UTC)
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {Date} - End of day in UTC
 */
const getEndOfDay = (date, timezone = DEFAULT_TIMEZONE) => {
  if (!date) return endOfDay(new Date());
  
  try {
    // Parse the date
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    // Convert to user's timezone
    const dateInUserTz = utcToZonedTime(dateObj, timezone);
    
    // Get end of day in user's timezone
    const endOfDayInUserTz = endOfDay(dateInUserTz);
    
    // Convert back to UTC
    return zonedTimeToUtc(endOfDayInUserTz, timezone);
  } catch (error) {
    console.error('Error getting end of day:', error);
    return endOfDay(new Date());
  }
};

/**
 * Debug a date showing UTC and user timezone representations
 * @param {Date|string} date - Date to debug
 * @param {string} timezone - IANA timezone (defaults to 'America/Chicago')
 * @returns {Object} - Debug information about the date
 */
const debugDate = (date, timezone = DEFAULT_TIMEZONE) => {
  if (!date) return { error: 'No date provided' };
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    return {
      original: date,
      parsed: dateObj.toString(),
      iso: dateObj.toISOString(),
      utcString: dateObj.toUTCString(),
      userTimezone: {
        timezone,
        dateTime: formatInTimeZone(dateObj, timezone, 'yyyy-MM-dd HH:mm:ss zzz'),
        date: formatInTimeZone(dateObj, timezone, 'yyyy-MM-dd'),
        time: formatInTimeZone(dateObj, timezone, 'HH:mm:ss'),
        offset: formatInTimeZone(dateObj, timezone, 'xxx')
      }
    };
  } catch (error) {
    console.error('Error debugging date:', error);
    return { error: 'Error debugging date', message: error.message };
  }
};

export {
  formatDate,
  formatTime,
  formatDateTime,
  isPastDate,
  isFutureDate,
  getRelativeDay,
  groupByDate,
  combineDateAndTime,
  getDatePart,
  getTimePart,
  isTruckScheduledForDate,
  doTimeSlotsOverlap,
  getStartOfDay,
  getEndOfDay,
  debugDate,
  // Re-export date-fns-tz functions for convenience
  zonedTimeToUtc,
  utcToZonedTime
};